import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  AccountingQreSummaryFilter,
  advancedFilter,
  Conditions,
  FilterField,
  GET_ACCOUNTING_QRE_SUMMARY,
  GetAccountingQreSummaryQuery,
  GetAccountingQreSummaryQueryVariables,
  keywordFilter,
  useSticky,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

export type AccountingQreSummary = NonNullable<GetAccountingQreSummaryQuery['getAccountingQreSummary']>;

type UseAccountingQreSummaryProps = {
  filterConditions?: Conditions<FilterField<AccountingQreSummaryFilter>>;
  searchKeyword?: string | null;
};

type UseAccountingQreSummaryResult =
  | {
      loading: true;
      accountingQreSummary: null;
    }
  | {
      loading: false;
      accountingQreSummary: AccountingQreSummary;
    };

export const useAccountingQreSummary = ({
  filterConditions,
  searchKeyword,
}: UseAccountingQreSummaryProps = {}): UseAccountingQreSummaryResult => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetAccountingQreSummaryQuery, GetAccountingQreSummaryQueryVariables>(GET_ACCOUNTING_QRE_SUMMARY, {
    variables: {
      fiscalYearId,
      filter: {
        and: [keywordFilter(['name'], searchKeyword), advancedFilter<AccountingQreSummaryFilter>(filterConditions)],
      },
    },
  });

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const accountingQreSummary = stickyData?.getAccountingQreSummary ?? null;

  if (loading) {
    return {
      loading: true,
      accountingQreSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !accountingQreSummary) {
    throw createError('MissingAccountingQreSummary', 'No data returned from getAccountingQreSummary query');
  }

  return {
    loading,
    accountingQreSummary,
  };
};
